import {
  Button,
  FlexBox,
  Modal,
  space,
} from "@waldner-laboreinrichtungen/component-system";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Channel, Device, Incident } from "../view-model";
import { ErrorReportOne } from "./error-report-one";
import { ErrorReportTwo } from "./error-report-two";

type ErrorReportModalProps = {
  incident: Incident;
  device?: Device;
  /** Channels für die Label-Auflösung der Meldung (Kette ComponentName →
   * displayName → browseName, vom REST bereits aufgelöst). */
  channels?: Channel[];
  isOpenModal: boolean;
  setIsOpenModal: Dispatch<SetStateAction<boolean>>;
  updateIncidentStates: (
    incident: Incident,
    device: Device,
    action: "ack" | "confirm" | "report",
    comment?: string
  ) => Promise<void>;
};

export const ErrorReportModal = ({
  channels,
  device,
  incident,
  isOpenModal,
  setIsOpenModal,
  updateIncidentStates,
}: ErrorReportModalProps) => {
  const { t } = useTranslation();
  const [reportStep, setReportStep] = useState<1 | 2>(1);
  const [comment, setComment] = useState("");

  const trimmedComment = () => comment.trim();
  const hasComment = trimmedComment().length > 0;

  // Confirm + Report require an operator comment (audit trail context).
  // Ack is the quick-action — same Quittieren-Click wie in der Tabellen-
  // Zeile, kein Kommentar verlangt.
  const onAck = async () => {
    if (!device) return;
    await updateIncidentStates(
      incident,
      device,
      "ack",
      trimmedComment() || undefined
    );
    closeAndReset();
  };

  const onConfirm = async () => {
    if (!device || !hasComment) return;
    await updateIncidentStates(incident, device, "confirm", trimmedComment());
    closeAndReset();
  };

  const onMarkReported = async () => {
    if (!device || !hasComment) return;
    // "Als gemeldet markieren" → echtes Report-Event (custom Essentim-Spec).
    await updateIncidentStates(incident, device, "report", trimmedComment());
    closeAndReset();
  };

  const closeAndReset = () => {
    setIsOpenModal(false);
    setReportStep(1);
    setComment("");
  };

  return (
    <Modal onClose={closeAndReset} open={isOpenModal}>
      <div>
        {reportStep === 1 && (
          <ErrorReportOne
            channels={channels}
            comment={comment}
            device={device}
            incident={incident}
            setComment={setComment}
          />
        )}
        {reportStep === 2 && (
          <ErrorReportTwo
            comment={comment}
            device={device}
            incident={incident}
            setComment={setComment}
          />
        )}
      </div>
      <FlexBox gap={space(4)} justifyContent="flex-end" marginTop={space(9)}>
        <Button onClick={closeAndReset} variant="secondary">
          {t("report.close")}
        </Button>
        {reportStep === 1 ? (
          <>
            {/*
              Jeder Operator-Action-Button erscheint nur, wenn er auch
              tatsächlich noch was bewirkt:
                - Quittieren: solange noch nicht acked UND noch nicht
                  confirmed. KEIN active-Check: ein ungequittierter Alarm,
                  der (ohne Ack) in den Normalzustand zurückkehrt, wird per
                  OPC-UA A&C auf einen ConditionBranch ausgelagert (active=
                  false) und ist weiterhin quittierbar. Spiegelt die Leiste
                  (lads-detail-incidents) — sonst zeigt die Liste „Quittieren",
                  der Dialog aber nicht.
                - Bestätigen: nur wenn aufgelöst UND noch nicht confirmed
                - Fehler melden (→ Step 2 → Report): nur wenn noch nicht
                  reported
              Damit verschwindet ein Button sobald die Aktion bereits in
              der History steht — analog zur "Behoben"-Anzeige.
            */}
            {/* non-strict: resolved = erledigt → kein Quittieren mehr (kein Branch wartet). */}
            {!(incident.strictAudited === false && incident.active === false) &&
              !incident.acknowledged &&
              !incident.confirmed && (
                <Button onClick={onAck} variant="secondary">
                  {t("incident.acknowledge")}
                </Button>
              )}
            {incident.strictAudited !== false &&
              incident.active === false &&
              incident.acknowledged &&
              !incident.confirmed && (
                <Button
                  disabled={!hasComment}
                  onClick={onConfirm}
                  variant="primary"
                >
                  {t("report.confirm")}
                </Button>
              )}
            {!incident.reported && (
              <Button onClick={() => setReportStep(2)} variant="primary">
                {t("report.reporterror")}
              </Button>
            )}
          </>
        ) : (
          <Button
            disabled={!hasComment}
            onClick={onMarkReported}
            variant="primary"
          >
            {t("report.markreported")}
          </Button>
        )}
      </FlexBox>
    </Modal>
  );
};
